import React, { useEffect, useMemo, useRef, useState } from "react";
import style from "./booking.module.scss";
import CarIcon from "../../../components/shared/icons/CarIcon";
import cn from "classnames";
import { Driver as DriverApi } from "../../../services";
import Driver from "../../../components/orders/driver";
import { requestErrorDisplay } from "../../../utils";
import Loading from "../../../components/shared/Loading/Loading";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CloseIconNew from "../../../components/shared/icons/CloseIconNew";
import { t } from "i18next";
import CustomButton from "../../../components/custom/custom-button/CustomButton";
import UserProfileIcon from "../../../components/shared/icons/UserProfileIcon";
import { useClickOutside } from "../../../hooks/useClickOutside";

const OrderBookingCard = ({
  driver,
  index,
  orderId,
  isHide = false,
  isBooked = false,
  companyId,
  getDataSubmit,
  onDelete,
  isOpenModal,
  setIsOpenModal,
  onSubmitBooking,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingRef = useRef(null);
  const driverTitleRef = useRef(null);

  const [isLoading, setLoading] = useState(false);
  const [transports, setTransports] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [activeDriver, setActiveDriver] = useState(null);
  const [activeTransport, setActiveTransport] = useState(null);

  useClickOutside(bookingRef, () => setIsOpenModal(false));

  useEffect(() => {
    if (isOpenModal) {
      getItems();
    }
  }, [isOpenModal]);

  const handleGetDriver = (e) => {
    if (!e.isFree) {
      return;
    }
    if (activeDriver?.id === e.id) {
      setActiveDriver(null);
    } else {
      setActiveDriver(e);
    }
  };
  const handleGetTransport = (e) => {
    if (!e.isFree) {
      return;
    }
    if (activeTransport?.id === e.id) {
      setActiveTransport(null);
    } else {
      setActiveTransport(e);

      if (!activeDriver) {
        setTimeout(() => {
          driverTitleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  };

  const handleSubmit = async () => {
    if (!activeDriver || !activeTransport) {
      toast.error(t("orderBooking.selectDriverAndTransport"));
      return;
    }

    try {
      if (onSubmitBooking) {
        await onSubmitBooking();
      }
    } catch (error) {
      console.error("Ошибка при отправке бронирования:", error);
      toast.error(t("orderBooking.errorBooking"));
    }
  };

  const getItems = async () => {
    setLoading(true);
    await DriverApi.getDriversListByExpeditor(companyId)
      .then((res) => res.data)
      .then((data) => {
        const sortedDrivers = (data || []).sort((a, b) => {
          return (b.isFree === true ? 1 : 0) - (a.isFree === true ? 1 : 0);
        });
        setDrivers(sortedDrivers);
      })
      .catch((e) => {
        requestErrorDisplay(e);
      });

    await DriverApi.getVehicleByExpeditor(companyId)
      .then((res) => res.data)
      .then((data) => {
        const sortedTransports = (data || []).sort((a, b) => {
          return (b.isFree === true ? 1 : 0) - (a.isFree === true ? 1 : 0);
        });
        setTransports(sortedTransports);
      })
      .catch((e) => {
        requestErrorDisplay(e);
      });
    setLoading(false);
  };

  useEffect(() => {
    const autoSubmit = async () => {
      if (activeDriver && activeTransport) {
        try {
          await getDataSubmit({
            driver: activeDriver,
            transport: activeTransport,
          });
        } catch (error) {
          console.error("Ошибка при автоназначении:", error);
          toast.error(t("orderBooking.errorBooking"));
        }
      }
    };

    autoSubmit();
  }, [activeDriver, activeTransport]);

  return (
    <>
      <div className={cn(style.card, { [style.isLoading]: isLoading })} ref={bookingRef}>
        <div className={style.card__close_icon}>
          <CloseIconNew onClick={() => setIsOpenModal(false)} style={{ cursor: "pointer" }} />
        </div>
        {isLoading && <Loading inBlock />}
        <div className={cn(style.w100)}>
          <div className={cn(style.card__content, style.drivers, { [style.hide]: isLoading })}>
            <div className={style.drivers__title}>{t("orderBooking.selectTransport")}</div>
            <div className={style.drivers__items}>
              {transports.map((t) => (
                <Driver.DriverTransport
                  key={t.id}
                  item={t}
                  isActive={activeTransport?.id === t.id}
                  onClick={() => handleGetTransport(t)}
                />
              ))}
              <Driver.DriverTransport
                isCreate
                onClick={() => navigate(`/cars/create?redirectUrl=${location.pathname}`)}
              />
            </div>
            <hr />
            <div className={style.drivers__title} ref={driverTitleRef}>
              {t("orderBooking.selectDriver")}
            </div>
            <div className={style.drivers__items}>
              {drivers.map((d) => (
                <Driver.DriverCard
                  key={d.id}
                  item={d}
                  isActive={activeDriver?.id === d.id}
                  onClick={() => handleGetDriver(d)}
                />
              ))}
              <Driver.DriverCard
                isCreate
                onClick={() => navigate(`/drivers/create?redirectUrl=${location.pathname}`)}
              />
            </div>
          </div>
        </div>

        <div className={cn(style.card__selected, { [style.hide]: isLoading })}>
          <div className={style.driversWrapper}>
            <div className={style.drivers__section}>
              <div className={style.drivers__label}>{t("orderInfo.ordersDriverInfo.transportLabel")}</div>
              {activeTransport ? (
                <div className={style.drivers__content}>
                  <CarIcon fill={"#C3C5CA"} />
                  <div className={style.drivers__details}>
                    <span className={style.drivers__carModel}>
                      {activeTransport.carModel} {activeTransport.carNumber}
                    </span>
                    <span className={style.drivers__label}>{activeTransport.vehicleTypeName}</span>
                  </div>
                </div>
              ) : (
                <div className={style.drivers__content}>
                  <span className={style.drivers__carModel}>
                    {t("ordersCreateDesktop.detailsSection.transportTypePlaceholder")}
                  </span>
                </div>
              )}
            </div>

            <div className={style.drivers__section}>
              <div className={style.drivers__label}>{t("orderInfo.ordersDriverInfo.driverLabel")}</div>
              {activeDriver ? (
                <div className={style.drivers__content}>
                  <UserProfileIcon />
                  <div className={style.drivers__details}>
                    <span className={style.drivers__carModel}>
                      {activeDriver.surname ? activeDriver.surname.charAt(0).toUpperCase() +
                        activeDriver.surname.slice(1).toLowerCase() : ''}{" "}
                      {activeDriver.name ? activeDriver.name.charAt(0).toUpperCase() + '.' : ''}.
                      {activeDriver.patronymic ? activeDriver.patronymic.charAt(0).toUpperCase() + '.' : ''}
                    </span>
                    <span className={style.drivers__label}>{activeDriver?.phoneNumber ? +activeDriver.phoneNumber : "-"}</span>
                  </div>
                </div>
              ) : (
                <div className={style.drivers__content}>
                  <span className={style.drivers__carModel}>{t("orderBooking.selectedDriver")}</span>
                </div>
              )}
            </div>
          </div>
          <CustomButton
            isFall
            text={t("orderBooking.button.assign")}
            onClick={handleSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default OrderBookingCard;
